import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { AssignmentInd, FileUploadOutlined } from '@mui/icons-material';
import { LuImageOff } from 'react-icons/lu';
import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import { FileTypeIcon } from '@/components/media/file-type-icon';
import {
  mapMediaItemStatusToneToIconColor,
  resolveMediaItemAggregateStatus,
  resolveMediaItemRowDisplayStatus,
  resolveMediaItemRowProcessTone,
  resolveMediaStatusLabelKey,
  type MediaItemAggregateStatusResult,
} from '@/helpers/media-item-aggregate-status.helper';
import { buildProcessingInsightTooltip } from '@/helpers/media-processing-insight-tooltip.helper';
import { isAssetApprovable } from '@/helpers/media-processing-insight.helper';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { MediaItemProcessingSpinner } from './media-item-processing-spinner';

type MediaItemStatusIconProps = {
  media: MediaDto;
  collapsedRowIconAsset?: MediaAssetDto;
  canUpload: boolean;
  clickable: boolean;
  onClick?: (event: MouseEvent) => void;
  /** Contributor role-details / contributions: action-required upload states use warning. */
  contributorFacing?: boolean;
  /**
   * Upload in flight or waiting for media/processing status to catch up after upload.
   * Shows the processing hourglass so the row icon does not flash the idle upload/file icon.
   */
  busy?: boolean;
  /** When the media row is expanded, show an upload affordance instead of the playout asset status icon. */
  expanded?: boolean;
};

function actionHintKey(
  status: MediaItemAggregateStatusResult['status'],
  options: { contributorFacing: boolean; canUpload: boolean },
): TranslationKey | undefined {
  if (status === 'requested') {
    // Contributors upload into requested slots; managers unrequest.
    if (options.contributorFacing && options.canUpload) {
      return 'common.upload';
    }
    return 'media.confirmUnrequestTitle';
  }
  return undefined;
}

function buildStatusTooltipLines(
  statusResult: MediaItemAggregateStatusResult,
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
  options: { contributorFacing: boolean; canUpload: boolean; incompleteUpload?: boolean },
): string[] {
  const lines = [
    t(
      resolveMediaStatusLabelKey(statusResult, {
        contributorFacing: options.contributorFacing,
        canUpload: options.canUpload,
      }),
    ),
  ];
  if (statusResult.insight && statusResult.insight.readiness !== 'not_applicable') {
    const processingTooltip = buildProcessingInsightTooltip(statusResult.insight, t);
    for (const line of processingTooltip.split('\n')) {
      if (!lines.includes(line)) {
        lines.push(line);
      }
    }
  }
  if (options.incompleteUpload) {
    const retryText = t('media.uploadStatus.didNotFinish');
    if (!lines.includes(retryText)) {
      lines.push(retryText);
    }
  }
  const hint = actionHintKey(statusResult.status, {
    contributorFacing: options.contributorFacing,
    canUpload: options.canUpload,
  });
  if (hint) {
    const hintText = t(hint);
    if (!lines.includes(hintText)) {
      lines.push(hintText);
    }
  }
  return lines;
}

export function MediaItemStatusIcon({
  media,
  collapsedRowIconAsset,
  canUpload,
  clickable,
  onClick,
  contributorFacing = false,
  busy = false,
  expanded = false,
}: MediaItemStatusIconProps) {
  const t = useTranslations();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const aggregate = useMemo(() => resolveMediaItemAggregateStatus(media), [media]);
  // Collapsed row: surface latest process state; warn/error severity stays on trailing affordance + slot indicator.
  const statusResult = useMemo(
    () => resolveMediaItemRowDisplayStatus(media, collapsedRowIconAsset),
    [collapsedRowIconAsset, media],
  );
  const showRequestedManagerBadge = !busy && aggregate.status === 'requested' && !(contributorFacing && canUpload);
  // Prefer processing/queued spinner over the expanded upload affordance — otherwise an open row
  // keeps showing the orange upload icon while preprocess is in flight.
  const showBusySpinner = busy || statusResult.showSpinner;
  const showExpandedUpload = expanded && !showRequestedManagerBadge && !showBusySpinner;

  // Primary = action required. If an approved or approvable asset already exists, use active (inherit).
  const hasApprovedOrApprovableAsset = useMemo(
    () => (media.assets ?? []).some((asset) => asset.isApproved || isAssetApprovable(asset)),
    [media.assets],
  );

  const processTone = resolveMediaItemRowProcessTone(statusResult, { contributorFacing, canUpload });
  const iconColor = showExpandedUpload
    ? !canUpload
      ? 'disabled'
      : hasApprovedOrApprovableAsset
        ? 'inherit'
        : 'primary'
    : mapMediaItemStatusToneToIconColor(processTone);

  // Modals/overlays do not fire mouseleave on the trigger — dismiss on any outside pointer.
  useEffect(() => {
    if (!tooltipOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (triggerRef.current?.contains(event.target as Node)) {
        return;
      }
      setTooltipOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [tooltipOpen]);

  const tooltip = useMemo(() => {
    if (showExpandedUpload) {
      return canUpload ? t('common.upload') : t('media.uploadNotPermitted');
    }
    if (busy && !statusResult.showSpinner) {
      return t('media.processing.status.processing');
    }
    return buildStatusTooltipLines(statusResult, t, {
      contributorFacing,
      canUpload,
      incompleteUpload: !busy && !!media.incompleteUpload,
    }).join('\n');
  }, [busy, canUpload, contributorFacing, media.incompleteUpload, showExpandedUpload, statusResult, t]);

  // Tooltip title is React nodes, so MUI cannot derive a name — label the control explicitly.
  const accessibleLabel = tooltip.split('\n')[0] || t('media.noAsset' as TranslationKey);

  const frameSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    flexShrink: 0,
  } as const;

  const handleClick = (event: MouseEvent) => {
    setTooltipOpen(false);
    (event.currentTarget as HTMLElement).blur();
    onClick?.(event);
  };

  const frame = (children: ReactNode) => (
    <Tooltip
      title={tooltip.split('\n').map((line, index) => (
        <Box key={`${index}-${line}`} component="span" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
          {line}
        </Box>
      ))}
      placement="top-start"
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      // Avoid stuck hover tooltips when a modal covers the trigger without mouseleave.
      leaveDelay={0}
      disableFocusListener
      disableInteractive
    >
      {clickable ? (
        <IconButton
          ref={(node: HTMLButtonElement | null) => {
            triggerRef.current = node;
          }}
          aria-label={accessibleLabel}
          onClick={handleClick}
          size="small"
          sx={{ ...frameSx, p: 0 }}
        >
          {children}
        </IconButton>
      ) : (
        <Box
          ref={(node: HTMLDivElement | null) => {
            triggerRef.current = node;
          }}
          role="img"
          aria-label={accessibleLabel}
          sx={frameSx}
        >
          {children}
        </Box>
      )}
    </Tooltip>
  );

  // Contributor view: requested = awaiting their upload (not the manager "request" badge).
  if (showRequestedManagerBadge) {
    return frame(
      <AssignmentInd
        color={mapMediaItemStatusToneToIconColor(
          resolveMediaItemRowProcessTone(aggregate, { contributorFacing, canUpload }),
        )}
        sx={{ fontSize: 24 }}
      />,
    );
  }

  if (showBusySpinner) {
    return frame(
      <MediaItemProcessingSpinner status={statusResult.showSpinner ? statusResult.insight?.status : undefined} />,
    );
  }

  if (showExpandedUpload) {
    return frame(<FileUploadOutlined color={iconColor} sx={{ fontSize: 24 }} />);
  }

  if (collapsedRowIconAsset?.fileType) {
    return frame(<FileTypeIcon fileType={collapsedRowIconAsset.fileType} color={iconColor} sx={{ fontSize: 24 }} />);
  }

  if (canUpload) {
    return frame(<FileUploadOutlined color={iconColor} sx={{ fontSize: 24 }} />);
  }

  // No upload permission and no asset yet — empty placeholder (not actionable).
  return frame(
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'action.disabled',
        lineHeight: 1,
      }}
    >
      <LuImageOff size={20} aria-hidden />
    </Box>,
  );
}
